/** Detailseite einer Pflanze: Aufgaben, Steckbrief, Verlauf, Foto-Tagebuch. */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ChevronLeft, Camera, Pencil } from 'lucide-react';
import {
  SOIL_LABEL,
  allTaskTypes,
  findSpecies,
  fmtAgo,
  type CareEvent,
  type TaskStatus,
} from '@pflanzen/shared';
import { useStore, undoEvent } from '../lib/store';
import { useNow, useStatuses } from '../lib/hooks';
import { uploadPhoto } from '../lib/photos';
import { CheckSheet } from '../ui/CheckSheet';
import { PlantAvatar, TaskRow } from '../ui/TaskRow';
import { PhotoDiary } from '../ui/PhotoDiary';
import { Card, SectionTitle } from '../ui/kit';

const OUTCOME_TEXT: Record<CareEvent['outcome'], string> = {
  done: 'erledigt',
  still_moist: 'noch feucht – nicht gegossen',
  skipped: 'ausgelassen',
  snoozed: 'auf später verschoben',
};

const SOURCE_TEXT: Record<CareEvent['source'], string> = {
  app: '',
  shortcut: ' · Kurzbefehl',
  nfc: ' · NFC',
  siri: ' · Siri',
  push: ' · Mitteilung',
  import: ' · Import',
  sitter: ' · Pflanzen-Sitter',
};

/** Steckbrief-Zeile: Symbol in eigener Spalte, Text bricht sauber darunter um. */
function Fact({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2.5">
      <span className="w-5 shrink-0 text-center">{icon}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function PlantPage() {
  const { id } = useParams();
  const { state } = useStore();
  const now = useNow();
  const statuses = useStatuses(state, now);
  const [checking, setChecking] = useState<TaskStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const plant = state?.plants.find((p) => p.id === id);
  const events = useMemo(
    () => (state?.events ?? []).filter((e) => e.plantId === id && !e.deletedAt).sort((a, b) => b.at.localeCompare(a.at)),
    [state, id],
  );
  if (!state) return null;
  if (!plant) return <p className="pt-safe px-4">Pflanze nicht gefunden.</p>;

  const tz = state.settings.timezone;
  const species = findSpecies(plant.speciesKey, state.species);
  const room = state.rooms.find((r) => r.id === plant.roomId);
  const mine = statuses.filter((s) => s.plant.id === plant.id);
  const types = new Map(allTaskTypes(state.taskTypes).map((t) => [t.key, t]));
  const photos = state.photos.filter((p) => p.plantId === plant.id).sort((a, b) => a.takenAt.localeCompare(b.takenAt));

  const addPhoto = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      await uploadPhoto(plant.id, file);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 pb-6">
      <header className="pt-safe flex items-center justify-between pb-2">
        <Link to="/pflanzen" className="-ml-2 flex items-center gap-0.5 px-2 py-2 text-[15px] font-semibold text-moss">
          <ChevronLeft className="h-5 w-5" /> Pflanzen
        </Link>
        <Link to={`/pflanzen/${plant.id}/bearbeiten`} aria-label="Bearbeiten" className="grid h-10 w-10 place-items-center rounded-full bg-card">
          <Pencil className="h-4 w-4" />
        </Link>
      </header>

      <div className="rise flex items-center gap-4">
        <PlantAvatar photoId={plant.coverPhotoId} emoji={plant.emoji} size={88} />
        <div className="min-w-0">
          <h1 className="font-display text-[28px] leading-tight">{plant.name}</h1>
          <p className="text-sm text-muted">
            {species ? <i>{species.name}</i> : 'Eigene Pflanze'} · {room?.name ?? ''}
          </p>
          <label className="mt-2 inline-flex min-h-9 items-center gap-1.5 rounded-full bg-moss-soft px-3 text-sm font-semibold text-moss">
            <Camera className="h-4 w-4" /> {busy ? 'Speichere …' : 'Foto'}
            <input type="file" accept="image/*" capture="environment" className="hidden" disabled={busy} onChange={(e) => void addPhoto(e.target.files?.[0])} />
          </label>
        </div>
      </div>

      <SectionTitle>Pflege</SectionTitle>
      <div className="space-y-2.5">
        {mine.map((s) => (
          <TaskRow key={s.task.id} status={s} onCheck={setChecking} onPlant />
        ))}
        {mine.length === 0 && <p className="px-1 text-sm text-muted">Keine Aufgaben. Über „Bearbeiten“ hinzufügen.</p>}
      </div>

      {species && (
        <>
          <SectionTitle>Steckbrief</SectionTitle>
          <Card className="space-y-2 text-sm">
            {species.light && <Fact icon="☀️">{species.light}</Fact>}
            <Fact icon="💧">
              Gießen, wenn die Erde {SOIL_LABEL[species.idealSoil].toLowerCase()} ist (Fingertest {species.fingerDepthCm} cm tief).
            </Fact>
            <Fact icon="🌡️">
              {species.tempMinC}–{species.tempMaxC} °C · Luftfeuchte ideal ab {species.humidityTarget} %
              {species.draftSensitive ? ' · mag keine Zugluft' : ''}
            </Fact>
            {species.fluorideSensitive && (
              <Fact icon="🚰">Empfindlich auf Leitungswasser – am besten gefiltertes, Regen- oder über Nacht abgestandenes Wasser.</Fact>
            )}
            {species.toxicity && <Fact icon="⚠️">{species.toxicity}</Fact>}
            {species.tips.map((tip) => (
              <Fact key={tip} icon="•">
                <span className="text-ink/80">{tip}</span>
              </Fact>
            ))}
            {species.sourceUrl && (
              <a href={species.sourceUrl} target="_blank" rel="noreferrer" className="block text-xs text-muted underline">
                Quelle
              </a>
            )}
          </Card>
        </>
      )}

      <SectionTitle>Foto-Tagebuch</SectionTitle>
      <PhotoDiary photos={photos} plantName={plant.name} tz={tz} />

      <SectionTitle>Verlauf</SectionTitle>
      <Card className="divide-y divide-line py-1">
        {events.slice(0, 30).map((e) => {
          const t = types.get(e.typeKey);
          return (
            <div key={e.id} className="flex items-start gap-3 py-2.5">
              <span className="text-lg leading-none">{t?.icon ?? '•'}</span>
              <div className="min-w-0 flex-1 text-sm">
                <div>
                  <b>{t?.label ?? e.typeKey}</b> {OUTCOME_TEXT[e.outcome]}
                  {e.soil ? ` · Erde ${SOIL_LABEL[e.soil].toLowerCase()}` : ''}
                </div>
                <div className="text-xs text-muted">
                  {fmtAgo(new Date(e.at), now, tz)}
                  {SOURCE_TEXT[e.source]}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (confirm('Diesen Eintrag löschen?')) void undoEvent(e.id);
                }}
                className="-my-2 -mr-2 min-h-11 min-w-11 px-2 text-xs text-muted"
              >
                Löschen
              </button>
            </div>
          );
        })}
        {events.length === 0 && <p className="py-3 text-sm text-muted">Noch keine Einträge.</p>}
      </Card>
      {plant.notes && (
        <>
          <SectionTitle>Notizen</SectionTitle>
          <Card className="text-sm whitespace-pre-wrap">{plant.notes}</Card>
        </>
      )}
      <CheckSheet status={checking} onClose={() => setChecking(null)} />
    </div>
  );
}
